import { useMemo, useRef, useState } from "react";
import { QRCodeSVG } from "qrcode.react";
import { Download, QrCode } from "lucide-react";
import SEO from "../components/SEO";
import { CHAPTERS } from "../lib/data";

/**
 * QR Generator — a print-ready QR maker with Salty Moo branding around it.
 *
 * • Pick any destination (a chapter, /qr, or a custom URL).
 * • Preview updates live on a warm parchment card.
 * • One click downloads a 1200×1200 PNG suitable for books, tags, biz cards.
 */
export default function QRGenerator() {
  const site =
    typeof window !== "undefined" ? window.location.origin : "https://thesaltymoo.com";

  const [destination, setDestination] = useState("/qr");
  const [label, setLabel] = useState("Scan for the Pasture");
  const [tagline, setTagline] = useState("One Saltie Wink™ at a Time");
  const [customUrl, setCustomUrl] = useState("");
  const [downloading, setDownloading] = useState(false);
  const svgRef = useRef(null);

  const targetUrl = useMemo(() => {
    if (destination === "__custom__") {
      return customUrl || `${site}/qr`;
    }
    if (destination.startsWith("http")) return destination;
    return `${site}${destination}`;
  }, [destination, customUrl, site]);

  const downloadPng = async () => {
    setDownloading(true);
    try {
      // Grab the live SVG and paint it onto a canvas with branding.
      const svgEl = document.getElementById("saltymoo-qr-svg");
      if (!svgEl) return;
      const svgString = new XMLSerializer().serializeToString(svgEl);
      const svgBlob = new Blob([svgString], { type: "image/svg+xml;charset=utf-8" });
      const svgUrl = URL.createObjectURL(svgBlob);

      const img = new Image();
      img.crossOrigin = "anonymous";
      await new Promise((res, rej) => {
        img.onload = res;
        img.onerror = rej;
        img.src = svgUrl;
      });

      // 1200 × 1200 print-ready canvas.
      const size = 1200;
      const canvas = document.createElement("canvas");
      canvas.width = size;
      canvas.height = size;
      const ctx = canvas.getContext("2d");

      // Warm parchment background
      const bg = ctx.createLinearGradient(0, 0, 0, size);
      bg.addColorStop(0, "#F9F1E1");
      bg.addColorStop(1, "#EFE4CE");
      ctx.fillStyle = bg;
      ctx.fillRect(0, 0, size, size);

      // Subtle border
      ctx.strokeStyle = "#A87149";
      ctx.lineWidth = 6;
      ctx.strokeRect(30, 30, size - 60, size - 60);
      ctx.strokeStyle = "#C8A876";
      ctx.lineWidth = 2;
      ctx.strokeRect(50, 50, size - 100, size - 100);

      // Top label
      ctx.fillStyle = "#4B3A2F";
      ctx.textAlign = "center";
      ctx.font = 'bold 44px "Georgia", serif';
      ctx.fillText(label || "Scan for the Pasture", size / 2, 150);

      // Gold rule
      ctx.strokeStyle = "#C8A876";
      ctx.lineWidth = 3;
      ctx.beginPath();
      ctx.moveTo(size / 2 - 80, 190);
      ctx.lineTo(size / 2 + 80, 190);
      ctx.stroke();

      // QR — center a 700px square below the label
      const qrSize = 700;
      const qrX = (size - qrSize) / 2;
      const qrY = 230;
      // white padding behind the QR so it scans cleanly on the parchment
      ctx.fillStyle = "#FFFFFF";
      ctx.fillRect(qrX - 20, qrY - 20, qrSize + 40, qrSize + 40);
      ctx.drawImage(img, qrX, qrY, qrSize, qrSize);

      // Brand name below
      ctx.fillStyle = "#D62839";
      ctx.font = 'bold 32px "Georgia", serif';
      ctx.fillText("THE SALTY MOO™", size / 2, qrY + qrSize + 90);

      // Tagline
      ctx.fillStyle = "#4B3A2F";
      ctx.font = 'italic 28px "Georgia", serif';
      ctx.fillText(tagline || "One Saltie Wink™ at a Time", size / 2, qrY + qrSize + 140);

      URL.revokeObjectURL(svgUrl);

      // Trigger download
      const png = canvas.toDataURL("image/png");
      const a = document.createElement("a");
      const safe = (label || "salty-moo-qr")
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, "-")
        .replace(/^-|-$/g, "");
      a.download = `${safe || "salty-moo-qr"}.png`;
      a.href = png;
      document.body.appendChild(a);
      a.click();
      a.remove();
    } finally {
      setDownloading(false);
    }
  };

  return (
    <div
      className="parchment min-h-screen pt-20 pb-16 px-6"
      data-testid="qr-generator-page"
    >
      <SEO title="QR Code Maker (Admin)" description="Salty Moo QR maker." path="/qr-maker" />

      <div className="max-w-4xl mx-auto">
        <div className="flex items-baseline gap-3 mb-1">
          <QrCode size={22} className="text-[#4B3A2F]" />
          <h1 className="font-display text-4xl md:text-5xl text-[#4B3A2F]">
            QR Code Maker
          </h1>
        </div>
        <p className="font-script text-2xl text-[#4B3A2F] mb-1">
          print anywhere you like
        </p>
        <p className="text-sm text-[#6D5A50] italic mb-8 max-w-2xl">
          Pick where the QR should send people, tweak the label and tagline,
          then click <span className="font-body">Download PNG</span>. You&rsquo;ll
          get a 1200 × 1200 image with the Salty Moo frame around it — ready to
          drop onto book covers, business cards, product tags, anywhere.
        </p>

        <div className="grid md:grid-cols-2 gap-8">
          {/* Controls */}
          <div className="space-y-5">
            <div>
              <label className="text-xs uppercase tracking-[0.25em] text-[#4B3A2F] block mb-2">
                Send visitors to
              </label>
              <select
                data-testid="qr-destination-select"
                value={destination}
                onChange={(e) => setDestination(e.target.value)}
                className="w-full px-4 py-3 rounded-md border border-[#A87149] bg-[#FCF9F2] font-body text-[#2C2724]"
              >
                <option value="/qr">The QR welcome page (recommended)</option>
                <option value="/">The barn doors (home page)</option>
                {CHAPTERS.filter((c) => c.path !== "/").map((c) => (
                  <option key={c.path} value={c.path}>
                    Chapter {c.short} · {c.label}
                  </option>
                ))}
                <option value="/story-barn">The Story Barn shop</option>
                <option value="/front-porch">The Front Porch (subscribe)</option>
                <option value="__custom__">Something else (custom link)…</option>
              </select>
            </div>

            {destination === "__custom__" && (
              <div>
                <label className="text-xs uppercase tracking-[0.25em] text-[#4B3A2F] block mb-2">
                  Custom URL
                </label>
                <input
                  data-testid="qr-custom-url"
                  value={customUrl}
                  onChange={(e) => setCustomUrl(e.target.value)}
                  placeholder="https://your-link.com"
                  className="w-full px-4 py-3 rounded-md border border-[#A87149] bg-[#FCF9F2] font-body text-[#2C2724]"
                />
              </div>
            )}

            <div>
              <label className="text-xs uppercase tracking-[0.25em] text-[#4B3A2F] block mb-2">
                Label (top line)
              </label>
              <input
                data-testid="qr-label"
                value={label}
                onChange={(e) => setLabel(e.target.value)}
                className="w-full px-4 py-3 rounded-md border border-[#A87149] bg-[#FCF9F2] font-body text-[#2C2724]"
              />
            </div>

            <div>
              <label className="text-xs uppercase tracking-[0.25em] text-[#4B3A2F] block mb-2">
                Tagline (below the QR)
              </label>
              <input
                data-testid="qr-tagline"
                value={tagline}
                onChange={(e) => setTagline(e.target.value)}
                className="w-full px-4 py-3 rounded-md border border-[#A87149] bg-[#FCF9F2] font-body text-[#2C2724]"
              />
            </div>

            <div className="pt-2 space-y-2">
              <p className="text-xs text-[#6D5A50] italic">
                This QR will point to:{" "}
                <span
                  data-testid="qr-target-url"
                  className="font-body text-[#2C2724] break-all"
                >
                  {targetUrl}
                </span>
              </p>

              <button
                type="button"
                data-testid="qr-download-btn"
                onClick={downloadPng}
                disabled={downloading}
                className="btn-pasture inline-flex items-center gap-2 disabled:opacity-60"
              >
                <Download size={16} />
                {downloading ? "Preparing PNG…" : "Download PNG"}
              </button>
            </div>
          </div>

          {/* Live preview */}
          <div className="flex flex-col items-center">
            <div
              data-testid="qr-preview-card"
              className="w-full max-w-sm aspect-square rounded-md p-6 flex flex-col items-center justify-between"
              style={{
                background: "linear-gradient(180deg, #F9F1E1 0%, #EFE4CE 100%)",
                border: "3px double #C8A876",
                boxShadow: "0 15px 30px -12px rgba(75,58,47,0.35)",
              }}
            >
              <p className="text-lg md:text-xl font-display text-[#4B3A2F] text-center leading-tight">
                {label || "Scan for the Pasture"}
              </p>
              <div className="gold-rule w-16" />
              <div className="bg-white p-3 rounded-sm">
                <QRCodeSVG
                  id="saltymoo-qr-svg"
                  ref={svgRef}
                  value={targetUrl}
                  size={200}
                  level="M"
                  bgColor="#FFFFFF"
                  fgColor="#3E2723"
                />
              </div>
              <p className="text-[10px] tracking-[0.35em] uppercase text-[#D62839] font-body font-bold">
                THE SALTY MOO™
              </p>
              <p className="font-script text-lg text-[#4B3A2F] text-center leading-none">
                {tagline || "One Saltie Wink™ at a Time"}
              </p>
            </div>
            <p className="text-xs text-[#6D5A50] italic mt-4 text-center max-w-xs">
              Preview only — the downloaded PNG will be sharper and higher
              resolution (1200 × 1200).
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
